/**
 * Signature Pad Component
 * =======================
 * 
 * Core signature capture component that provides drawing functionality for digital signatures.
 * This component handles the actual signature input, drawing, and processing for form submissions.
 * 
 * Purpose:
 * - Provides canvas-based signature drawing interface
 * - Supports both touch and mouse input for cross-device compatibility
 * - Handles theme-aware signature rendering and processing
 * - Manages signature data conversion and storage
 * - Provides visual feedback for unsaved changes
 * 
 * Core Features:
 * - Real-time signature drawing with smooth line rendering
 * - Responsive canvas sizing with content preservation
 * - Theme-aware color adaptation (dark/light mode)
 * - Touch and mouse input support for mobile and desktop
 * - Unsaved changes detection and visual indicators
 * - Automatic signature processing for consistent output
 * 
 * Technical Implementation:
 * - HTML5 Canvas for drawing operations
 * - ResizeObserver for responsive canvas sizing
 * - Theme detection for automatic color adaptation
 * - Base64 encoding for signature data storage
 * - Service integration for signature persistence
 * 
 * Theme Support:
 * - Automatic dark/light mode detection
 * - Dynamic color adaptation for optimal visibility
 * - Consistent signature output regardless of input theme
 * - CSS variable integration for Ionic theming
 * 
 * @author MacSafety Development Team
 * @version 1.0
 */

import { Component, ElementRef, ViewChild, AfterViewInit, Input, Output, EventEmitter, OnDestroy } from '@angular/core';
import { SignatureService } from '../../../../../services/signature.service';

@Component({
  selector: 'app-signature-pad',
  template: `
    <!-- 
      SIGNATURE CONTAINER
      ====================
      Main container for the signature pad interface.
      Includes canvas drawing area and action buttons.
      Visual state changes based on unsaved changes.
    -->
    <div class="signature-container" [class.has-unsaved-changes]="hasUnsavedChanges">
      
      <!-- 
        SIGNATURE CANVAS
        =================
        HTML5 canvas element for signature drawing.
        Handles both touch and mouse input events.
        Responsive sizing with fixed height for consistency.
      -->
      <canvas #signaturePad
        (touchstart)="onTouchStart($event)"
        (touchmove)="onTouchMove($event)"
        (touchend)="onTouchEnd()"
        (mousedown)="onMouseDown($event)"
        (mousemove)="onMouseMove($event)"
        (mouseup)="onMouseUp()"
        (mouseleave)="onMouseUp()">
      </canvas>
      
      <!-- 
        SIGNATURE ACTIONS
        ==================
        Button controls for signature management.
        Clear button resets canvas, Save button processes signature.
        Save button shows visual feedback for unsaved changes.
      -->
      <div class="signature-actions">
        <!-- 
          CLEAR BUTTON
          =============
          Resets the signature canvas to blank state.
          Clears all drawn content and resets unsaved changes flag.
        -->
        <ion-button fill="clear" (click)="clear()">Clear</ion-button>
        
        <!-- 
          SAVE BUTTON
          ============
          Processes and saves the current signature.
          Disabled when no changes exist.
          Visual styling indicates unsaved changes state.
        -->
        <ion-button 
          fill="clear" 
          (click)="save()"
          [class.unsaved-changes]="hasUnsavedChanges"
          [disabled]="!hasUnsavedChanges">
          {{ hasUnsavedChanges ? 'Save*' : 'Save' }}
        </ion-button>
      </div>
    </div>
  `,
  styles: [`
    /* 
      SIGNATURE CONTAINER STYLING
      ============================
      Main container with responsive sizing and theme-aware borders.
      Visual feedback for unsaved changes with color and shadow effects.
    */
    .signature-container {
      display: inline-block;
      border: 2px solid var(--ion-color-medium, #ccc);
      border-radius: 4px;
      margin: 10px 0;
      width: 100%;
      min-width: 300px;
      max-width: 500px;
      background: var(--ion-color-light, #ffffff);
      transition: all 0.3s ease;
    }
    
    /* 
      UNSAVED CHANGES STYLING
      ========================
      Visual indication when signature has unsaved changes.
      Red border and shadow to draw user attention.
    */
    .signature-container.has-unsaved-changes {
      border-color: var(--ion-color-danger, #eb445a);
      box-shadow: 0 0 8px rgba(235, 68, 90, 0.3);
    }
    
    /* 
      CANVAS STYLING
      ===============
      Drawing area with responsive sizing and theme-aware appearance.
      Fixed height ensures consistent signature proportions.
    */
    canvas {
      width: 100%;
      height: 200px;
      touch-action: none;
      border: 1px solid var(--ion-color-medium, #ccc);
      border-radius: 4px;
      background: var(--ion-item-background, var(--ion-background-color, #ffffff));
    }
    
    /* 
      ACTION BUTTONS CONTAINER
      =========================
      Button layout with consistent spacing and theme-aware background.
      Right-aligned buttons for intuitive interaction flow.
    */
    .signature-actions {
      display: flex;
      justify-content: flex-end;
      padding: 8px;
      gap: 8px;
      background: var(--ion-item-background, var(--ion-background-color, #ffffff));
    }

    /* 
      UNSAVED CHANGES BUTTON STYLING
      ===============================
      Special styling for save button when changes exist.
      Blue background with white text and pulsing animation.
    */
    .signature-actions ion-button.unsaved-changes {
      --color: #ffffff;
      --background: var(--ion-color-primary, #3880ff);
      font-weight: bold;
      animation: pulse 2s infinite;
    }

    /* 
      PULSE ANIMATION
      ================
      Subtle pulsing effect for unsaved changes button.
      Draws attention without being distracting.
    */
    @keyframes pulse {
      0% {
        box-shadow: 0 0 0 0 rgba(56, 128, 255, 0.7);
      }
      70% {
        box-shadow: 0 0 0 10px rgba(56, 128, 255, 0);
      }
      100% {
        box-shadow: 0 0 0 0 rgba(56, 128, 255, 0);
      }
    }

    /* 
      DARK MODE SUPPORT - SYSTEM PREFERENCE
      ======================================
      Automatic dark mode styling based on system preference.
      Adjusts colors and backgrounds for optimal visibility.
    */
    @media (prefers-color-scheme: dark) {
      .signature-container {
        border-color: var(--ion-color-medium, #666);
        background: var(--ion-color-dark, #1e1e1e);
      }
      canvas {
        border-color: var(--ion-color-medium, #666);
        background: var(--ion-item-background, var(--ion-background-color, #1e1e1e));
      }
      .signature-actions {
        background: var(--ion-item-background, var(--ion-background-color, #1e1e1e));
      }
    }

    /* 
      DARK MODE SUPPORT - IONIC THEME CLASS
      ======================================
      Manual dark mode support for Ionic theme switching.
      Ensures consistent appearance when theme is manually toggled.
    */
    :host-context(.dark) .signature-container {
      border-color: var(--ion-color-medium, #666);
      background: var(--ion-color-dark, #1e1e1e);
    }
    :host-context(.dark) canvas {
      border-color: var(--ion-color-medium, #666);
      background: var(--ion-item-background, var(--ion-background-color, #1e1e1e));
    }
    :host-context(.dark) .signature-actions {
      background: var(--ion-item-background, var(--ion-background-color, #1e1e1e));
    }
  `]
})
export class SignaturePadComponent implements AfterViewInit, OnDestroy {
  
  // ============================================================================
  // VIEW CHILD REFERENCES
  // ============================================================================
  // References to DOM elements for direct manipulation
  
  /** 
   * Reference to the signature canvas element.
   * Used for drawing operations and canvas manipulation.
   * Provides access to canvas context and properties.
   */
  @ViewChild('signaturePad') signaturePad!: ElementRef<HTMLCanvasElement>;
  
  // ============================================================================
  // INPUT/OUTPUT PROPERTIES
  // ============================================================================
  // Component interface for data flow and event handling
  
  /** 
   * Unique identifier for the signature field.
   * Used to associate signature data with specific form fields.
   * Provided by parent component based on form requirements.
   */
  @Input() identifier: string = '';
  
  /** 
   * Event emitter for signature saved events.
   * Notifies parent components when signature is successfully saved.
   * Emits base64 encoded signature data.
   */
  @Output() signatureSaved = new EventEmitter<string>();

  // ============================================================================
  // PRIVATE PROPERTIES
  // ============================================================================
  // Internal state management and canvas operations
  
  /** 
   * Canvas rendering context for drawing operations.
   * Handles all drawing commands and canvas state management.
   */
  private ctx!: CanvasRenderingContext2D;
  
  /** 
   * Flag indicating if user is currently drawing.
   * Prevents drawing operations when not actively drawing.
   */
  private isDrawing: boolean = false;
  
  /** 
   * Last X coordinate for line drawing.
   * Used to create smooth lines between drawing points.
   */
  private lastX: number = 0;
  
  /** 
   * Last Y coordinate for line drawing.
   * Used to create smooth lines between drawing points.
   */
  private lastY: number = 0;
  
  /** 
   * Resize observer for responsive canvas sizing.
   * Monitors container size changes and adjusts canvas accordingly.
   */
  private resizeObserver: ResizeObserver;
  
  /** 
   * Flag to prevent initial resize from triggering content restoration.
   * Ensures canvas setup completes before handling resize events.
   */
  private initialResizeDone = false;
  
  /** 
   * Public flag indicating if signature has unsaved changes.
   * Used by template for visual feedback and button state management.
   */
  public hasUnsavedChanges: boolean = false;

  constructor(private signatureService: SignatureService) {
    // Initialize resize observer for responsive canvas sizing
    this.resizeObserver = new ResizeObserver(() => {
      if (this.initialResizeDone) {
        this.resizeCanvas();
      }
    });
  }

  // ============================================================================
  // LIFECYCLE METHODS
  // ============================================================================
  // Component initialization and cleanup
  
  /**
   * Lifecycle hook that initializes the component after view initialization.
   * Sets up canvas context, initial sizing, and theme detection.
   * Called automatically by Angular after view is initialized.
   */
  ngAfterViewInit() {
    const canvas = this.signaturePad.nativeElement;
    this.ctx = canvas.getContext('2d')!;
    
    // Initial setup with fixed size
    this.resizeCanvas();
    this.initialResizeDone = true;
    
    // Observe container size changes for responsive behavior
    this.resizeObserver.observe(canvas.parentElement!);

    // Listen for theme changes to update canvas appearance
    this.setupThemeListener();
  }

  /**
   * Lifecycle hook that cleans up the component.
   * Disconnects resize observer to prevent memory leaks.
   * Called automatically by Angular before component destruction.
   */
  ngOnDestroy() {
    this.resizeObserver.disconnect();
  }

  // ============================================================================
  // THEME DETECTION AND MANAGEMENT
  // ============================================================================
  // Methods for detecting and responding to theme changes
  
  /**
   * Sets up listeners for theme changes.
   * Monitors both system preference changes and Ionic theme class changes.
   * Ensures canvas appearance adapts to current theme automatically.
   */
  private setupThemeListener() {
    // Listen for system theme changes
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    mediaQuery.addEventListener('change', () => {
      this.updateCanvasStyle();
    });

    // Listen for Ionic theme changes (if using toggleDarkTheme)
    const observer = new MutationObserver(() => {
      this.updateCanvasStyle();
    });
    
    observer.observe(document.body, {
      attributes: true,
      attributeFilter: ['class']
    });
  }

  /**
   * Checks if dark mode is currently active.
   * Prioritizes Ionic theme class over system preference.
   * Ensures consistent theme detection across different switching methods.
   * 
   * @returns true if dark mode is active, false otherwise
   */
  private isDarkMode(): boolean {
    // Check for Ionic dark class (manual theme switching)
    if (document.body.classList.contains('dark')) {
      return true;
    }
    
    // Check for system preference (automatic theme switching)
    return window.matchMedia('(prefers-color-scheme: dark)').matches;
  }

  /**
   * Gets the appropriate stroke color for the current theme.
   * Ensures optimal visibility in both light and dark modes.
   * 
   * @returns CSS color string for stroke rendering
   */
  private getStrokeColor(): string {
    return this.isDarkMode() ? '#ffffff' : '#000000';
  }

  /**
   * Gets the appropriate background color for the current theme.
   * Uses CSS variables when available, falls back to theme-appropriate defaults.
   * 
   * @returns CSS color string for canvas background
   */
  private getBackgroundColor(): string {
    if (this.isDarkMode()) {
      // Try to get CSS variable value, fallback to dark color
      const computedStyle = getComputedStyle(document.documentElement);
      return computedStyle.getPropertyValue('--ion-background-color').trim() || '#1e1e1e';
    } else {
      // Try to get CSS variable value, fallback to white
      const computedStyle = getComputedStyle(document.documentElement);
      return computedStyle.getPropertyValue('--ion-background-color').trim() || '#ffffff';
    }
  }

  /**
   * Updates canvas styling based on current theme.
   * Applies appropriate colors for stroke and background.
   * Called when theme changes are detected.
   */
  private updateCanvasStyle() {
    if (!this.ctx) return;
    
    this.ctx.strokeStyle = this.getStrokeColor();
    
    // Update canvas background
    const canvas = this.signaturePad.nativeElement;
    canvas.style.backgroundColor = this.getBackgroundColor();
  }

  // ============================================================================
  // CANVAS RESIZING AND SETUP
  // ============================================================================
  // Methods for responsive canvas sizing and content preservation
  
  /**
   * Resizes the canvas to match container dimensions.
   * Preserves existing drawing content during resize operations.
   * Applies theme-appropriate styling after resize.
   */
  private resizeCanvas() {
    const canvas = this.signaturePad.nativeElement;
    const container = canvas.parentElement!;
    const rect = container.getBoundingClientRect();
    
    // Store current drawing if canvas has been initialized
    let imageData: ImageData | null = null;
    if (canvas.width > 0 && canvas.height > 0) {
      try {
        imageData = this.ctx.getImageData(0, 0, canvas.width, canvas.height);
      } catch (e) {
        // Ignore errors during initial setup
      }
    }
    
    // Set canvas size to match container size
    canvas.width = rect.width;
    canvas.height = 200; // Fixed height for consistent signature proportions
    
    // Reset context properties after resize
    this.ctx.lineWidth = 2;
    this.ctx.lineCap = 'round';
    this.ctx.lineJoin = 'round';
    this.ctx.globalCompositeOperation = 'source-over';
    
    // Apply theme-appropriate colors
    this.updateCanvasStyle();
    
    // Restore drawing if it existed
    if (imageData && imageData.data.some(pixel => pixel !== 0)) {
      this.ctx.putImageData(imageData, 0, 0);
      this.hasUnsavedChanges = true;
    }
  }

  // ============================================================================
  // INPUT COORDINATE PROCESSING
  // ============================================================================
  // Methods for converting input coordinates to canvas coordinates
  
  /**
   * Converts input event coordinates to canvas coordinates.
   * Handles both mouse and touch events with proper scaling.
   * Accounts for canvas size vs display size differences.
   * 
   * @param event - Mouse or touch event with client coordinates
   * @returns Object with x and y coordinates in canvas space
   */
  private getCoordinates(event: MouseEvent | Touch): { x: number, y: number } {
    const canvas = this.signaturePad.nativeElement;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    
    return {
      x: (event.clientX - rect.left) * scaleX,
      y: (event.clientY - rect.top) * scaleY
    };
  }

  // ============================================================================
  // TOUCH INPUT HANDLERS
  // ============================================================================
  // Methods for handling touch-based signature input
  
  /**
   * Handles touch start events for signature drawing.
   * Initializes drawing state and sets starting coordinates.
   * Prevents default touch behavior to avoid scrolling during drawing.
   * 
   * @param event - Touch event containing touch information
   */
  onTouchStart(event: TouchEvent) {
    event.preventDefault();
    const touch = event.touches[0];
    const coords = this.getCoordinates(touch);
    this.lastX = coords.x;
    this.lastY = coords.y;
    this.isDrawing = true;
    this.hasUnsavedChanges = true;
    
    // Start a new path
    this.ctx.beginPath();
    this.ctx.moveTo(this.lastX, this.lastY);
  }

  /**
   * Handles touch move events for signature drawing.
   * Continues drawing line from previous position to current position.
   * Only processes when actively drawing.
   * 
   * @param event - Touch event containing current touch position
   */
  onTouchMove(event: TouchEvent) {
    if (!this.isDrawing) return;
    event.preventDefault();
    const touch = event.touches[0];
    const coords = this.getCoordinates(touch);
    this.draw(coords.x, coords.y);
  }

  /**
   * Handles touch end events for signature drawing.
   * Stops drawing state when touch interaction ends.
   */
  onTouchEnd() {
    this.isDrawing = false;
  }

  // ============================================================================
  // MOUSE INPUT HANDLERS
  // ============================================================================
  // Methods for handling mouse-based signature input
  
  /**
   * Handles mouse down events for signature drawing.
   * Initializes drawing state and sets starting coordinates.
   * Similar to touch start but for mouse input.
   * 
   * @param event - Mouse event containing cursor position
   */
  onMouseDown(event: MouseEvent) {
    const coords = this.getCoordinates(event);
    this.lastX = coords.x;
    this.lastY = coords.y;
    this.isDrawing = true;
    this.hasUnsavedChanges = true;
    
    // Start a new path
    this.ctx.beginPath();
    this.ctx.moveTo(this.lastX, this.lastY);
  }

  /**
   * Handles mouse move events for signature drawing.
   * Continues drawing line from previous position to current position.
   * Only processes when actively drawing.
   * 
   * @param event - Mouse event containing current cursor position
   */
  onMouseMove(event: MouseEvent) {
    if (!this.isDrawing) return;
    const coords = this.getCoordinates(event);
    this.draw(coords.x, coords.y);
  }

  /**
   * Handles mouse up events for signature drawing.
   * Stops drawing state when mouse button is released.
   */
  onMouseUp() {
    this.isDrawing = false;
  }

  /**
   * Handles mouse leave events for signature drawing.
   * Stops drawing state when cursor leaves canvas area.
   * Prevents drawing from continuing outside canvas boundaries.
   */
  onMouseLeave() {
    this.isDrawing = false;
  }

  // ============================================================================
  // DRAWING OPERATIONS
  // ============================================================================
  // Core drawing logic for signature creation
  
  /**
   * Draws a line segment from last position to current position.
   * Creates smooth, connected lines for signature appearance.
   * Updates last position for next drawing operation.
   * 
   * @param currentX - Current X coordinate for drawing
   * @param currentY - Current Y coordinate for drawing
   */
  private draw(currentX: number, currentY: number) {
    this.ctx.lineTo(currentX, currentY);
    this.ctx.stroke();
    this.ctx.beginPath();
    this.ctx.moveTo(currentX, currentY);
    this.lastX = currentX;
    this.lastY = currentY;
  }

  // ============================================================================
  // SIGNATURE MANAGEMENT
  // ============================================================================
  // Methods for clearing, saving, and managing signature data
  
  /**
   * Clears the signature canvas.
   * Removes all drawn content and resets unsaved changes flag.
   * Provides clean slate for new signature input.
   */
  clear() {
    const canvas = this.signaturePad.nativeElement;
    this.ctx.clearRect(0, 0, canvas.width, canvas.height);
    this.hasUnsavedChanges = false;
  }

  /**
   * Saves the current signature to the signature service.
   * Processes signature data for consistent output regardless of input theme.
   * Converts canvas content to base64 PNG format.
   * Emits signature saved event for parent component notification.
   */
  async save() {
    const canvas = this.signaturePad.nativeElement;
    
    // Create a temporary canvas with white background for saving
    const tempCanvas = document.createElement('canvas');
    tempCanvas.width = canvas.width;
    tempCanvas.height = canvas.height;
    const tempCtx = tempCanvas.getContext('2d')!;
    
    // Fill with white background (standard for signatures)
    tempCtx.fillStyle = '#ffffff';
    tempCtx.fillRect(0, 0, tempCanvas.width, tempCanvas.height);
    
    // Always draw the signature in black on the white background
    if (this.isDarkMode()) {
      // In dark mode, the original canvas has white strokes on dark background
      // We need to convert these to black strokes
      const imageData = this.ctx.getImageData(0, 0, canvas.width, canvas.height);
      const data = imageData.data;
      
      // Set up the temp context to draw in black
      tempCtx.strokeStyle = '#000000';
      tempCtx.lineWidth = this.ctx.lineWidth;
      tempCtx.lineCap = this.ctx.lineCap;
      tempCtx.lineJoin = this.ctx.lineJoin;
      
      // Redraw the signature paths in black
      // We'll need to recreate the drawing paths
      // For now, let's use a simpler approach: convert white pixels to black
      for (let y = 0; y < canvas.height; y++) {
        for (let x = 0; x < canvas.width; x++) {
          const index = (y * canvas.width + x) * 4;
          const alpha = data[index + 3];
          
          if (alpha > 0) {
            // This pixel has a stroke, draw it in black
            tempCtx.fillStyle = '#000000';
            tempCtx.fillRect(x, y, 1, 1);
          }
        }
      }
    } else {
      // In light mode, the signature is already black, just copy it
      tempCtx.drawImage(canvas, 0, 0);
    }
    
    // Convert canvas to base64 data
    const base64Data = await this.signatureService.canvasToBase64(tempCanvas);
    
    // Create signature data object
    const signatureData = {
      base64Data,
      fileName: `${this.identifier}.png`,
      contentType: 'image/png',
      description: 'Signature'
    };
  
    // Save signature to service and emit event
    this.signatureService.updateSignatures(this.identifier, [signatureData]);
    this.signatureSaved.emit(base64Data);
    
    // Reset unsaved changes flag
    this.hasUnsavedChanges = false;
  }
}